import { useAscContext } from '@neo.tax/asc/context';
import { useUpdateContributorAllocationsMutation } from '@neo.tax/asc/hooks';
import { AscProject } from '@neo.tax/asc/hooks/useAscProjects';
import { Contributor } from '@neo.tax/asc/hooks/useContributor';
import { AscAllocationProjectDetails, AscContributorProjectAllocation } from '@neo.tax/asc/types';
import { getAscCapitalizationBadgeConfig } from '@neo.tax/asc/utils';
import {
  formatContributorName,
  AllocationModal,
  allocationsHaveChanges,
  getTotalAllocationPercent,
} from '@neo.tax/shared';

import { PerMonthAllocationSummary } from '../per-month-allocation-summary';
import {
  useContributorAllocationModalState,
  useFilteredProjectAllocations,
  useContributorMonthBreakdowns,
} from './hooks';

export interface BaseAllocationModalLoaderProps {
  isOpen: boolean;
  close: () => void;
  selectedAscProject?: AscProject;
  ascProjects: AscAllocationProjectDetails[];
}

interface AllocationModalLoaderProps extends BaseAllocationModalLoaderProps {
  contributor: Contributor;
  allAscContributorProjectAllocations: AscContributorProjectAllocation[] | null;
  allAscContributorProjectAllocationsLoading: boolean;
  filteredAscContributorProjectAllocations: AscContributorProjectAllocation[] | null;
  filteredAscContributorProjectAllocationsLoading: boolean;
  searchKeyword: string | null;
  setSearchKeyword: React.Dispatch<React.SetStateAction<string | null>>;
}

/**
 * Using two different components for the modal because the outer component needs to load the contributor and we
 * can't have conditional hooks. We need to load the contributor here in order to support opening contributor
 * allocations from the team member table in the project page.
 */
export const EditContributorAllocationModal = ({
  allAscContributorProjectAllocations,
  allAscContributorProjectAllocationsLoading,
  ascProjects,
  close,
  contributor,
  filteredAscContributorProjectAllocations,
  filteredAscContributorProjectAllocationsLoading,
  isOpen,
  searchKeyword,
  selectedAscProject,
  setSearchKeyword,
}: AllocationModalLoaderProps): React.ReactElement | null => {
  const { period } = useAscContext();

  const { modalAllocationPercents, updateNonProjectPercent, updateProjectAllocation } =
    useContributorAllocationModalState({
      isOpen,
      contributor,
      ascProjects,
      period,
      allAscContributorProjectAllocations,
      allAscContributorProjectAllocationsLoading,
      filteredAscContributorProjectAllocations,
      filteredAscContributorProjectAllocationsLoading,
      searchKeyword,
    });

  const { monthYearAllocationsInPeriod, periodAllocation } = useContributorMonthBreakdowns({
    allocationsInPeriod: modalAllocationPercents,
    period,
  });

  const { projectAllocations, selectedProjectAllocation } = useFilteredProjectAllocations({
    modalAllocationPercents,
    filteredAscContributorProjectAllocations,
    selectedAscProject,
  });

  const { loading: loadingUpdateAllocations, updateContributorAllocations } = useUpdateContributorAllocationsMutation({
    contributorId: contributor.id,
    onSuccess: close,
  });

  const submit = async (): Promise<void> => {
    await updateContributorAllocations(modalAllocationPercents);
  };

  const totalAllocationPercent = getTotalAllocationPercent(modalAllocationPercents);

  const hasUnsavedChanges = allocationsHaveChanges(modalAllocationPercents);

  const contributorName = formatContributorName(contributor.name);
  const error = totalAllocationPercent !== 100 ? `Total: ${totalAllocationPercent}%. Adjust projects to apply.` : null;

  return (
    <AllocationModal
      period={period}
      isOpen={isOpen}
      close={close}
      expenseName={contributorName}
      nonProjectPercent={modalAllocationPercents.nonProjectPercent.allocationPercent}
      nonProjectAllocationTitle="Non-Project Time"
      nonProjectAllocationHelpText={
        searchKeyword
          ? 'Editing the Non-Project Time will adjust all projects with allocation proportionally.'
          : undefined
      }
      endOfListWarningText="Use the search box above if you don't see your project."
      updateNonProjectPercent={updateNonProjectPercent}
      hasUnsavedChanges={hasUnsavedChanges}
      error={error}
      projectAllocations={projectAllocations}
      projectAllocationsLoading={
        filteredAscContributorProjectAllocationsLoading || allAscContributorProjectAllocationsLoading
      }
      updateProjectAllocation={updateProjectAllocation}
      loadingUpdateAllocations={loadingUpdateAllocations}
      submit={submit}
      getBadgeConfig={(projectAllocation) =>
        getAscCapitalizationBadgeConfig({
          capitalizationStatus: projectAllocation.capitalizationStatus,
          capitalizationStartDate: projectAllocation.capitalizationStartDate,
          capitalizationEndDate: projectAllocation.capitalizationEndDate,
          period,
        })
      }
      selectedProjectName={selectedAscProject?.name}
      selectedProjectAllocation={selectedProjectAllocation}
      searchKeyword={searchKeyword}
      setSearchKeyword={setSearchKeyword}
      SummaryComponent={
        <PerMonthAllocationSummary
          selectedAscProjectAllocationPercent={selectedProjectAllocation?.allocationPercent}
          selectedAscProjectName={selectedAscProject?.name}
          monthYearAllocationsInPeriod={monthYearAllocationsInPeriod}
          periodAllocation={periodAllocation}
          disabled={!!error}
        />
      }
    />
  );
};
